import React, { FormEvent, useState } from 'react'
import { useNotify } from 'react-admin'
import { Box, Grid, Modal, FormControlLabel, Checkbox } from '@mui/material'

import { createSubscription, SubscriptionStatus } from 'api/subscriptions'
import RoundedPanel, { Label } from 'common/RoundedPanel'
import { SumbitButtons } from 'common/SubmitButtons'
import { NormalTextField } from 'common/TextField'
import { CompanyRecord } from 'api/company'

interface CreateSubscriptionModalProps {
  record: CompanyRecord
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const statusOptions = [
  { id: SubscriptionStatus.ACTIVE, name: 'Active' },
  { id: SubscriptionStatus.INACTIVE, name: 'Inactive' },
  { id: SubscriptionStatus.TRIALING, name: 'Trialing' }
]

export const CreateSubscriptionModal = ({ record, open, onClose, onSuccess }: CreateSubscriptionModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isEndless, setIsEndless] = useState<boolean>(false)
  const notify = useNotify()

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const formData = new FormData(e.currentTarget)
      const startDate = formData.get('startDate') as string
      const startTime = formData.get('startTime') as string
      const endDate = isEndless ? undefined : (formData.get('endDate') as string)
      const endTime = isEndless ? undefined : (formData.get('endTime') as string)
      const status = (formData.get('status') as SubscriptionStatus) || SubscriptionStatus.ACTIVE

      if (!startDate) {
        notify('Start date is required', { type: 'warning' })
        setIsLoading(false)
        return
      }

      if (!isEndless && !endDate) {
        notify('End date is required when subscription is not endless', { type: 'warning' })
        setIsLoading(false)
        return
      }

      // Convert dates to ISO string format for API
      const startDateTime = new Date(`${startDate}T${startTime || '00:00'}:00.000Z`).toISOString()
      let endDateTime: string | undefined = undefined
      
      if (!isEndless && endDate) {
        endDateTime = new Date(`${endDate}T${endTime || '23:59'}:59.999Z`).toISOString()
        const endDateObj = new Date(endDateTime)
        const startDateObj = new Date(startDateTime)
        const now = new Date()
        
        if (endDateObj < now) {
          notify('End date cannot be in the past', { type: 'warning' })
          setIsLoading(false)
          return
        }
        
        if (startDateObj > endDateObj) {
          notify('Start date cannot be after end date', { type: 'warning' })
          setIsLoading(false)
          return
        }
      }

      await createSubscription(record.id.toString(), {
        startDate: startDateTime,
        endDate: endDateTime,
        status
      })

      notify('Subscription created successfully', { type: 'success' })
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error creating subscription:', error)
      notify('Failed to create subscription', { type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableAutoFocus={true}
    >
      <RoundedPanel modal>
        <h1>Create Subscription</h1>
        <Box height={32} />
        <Label noSelect margin>Company: </Label>{record.name}
        <Box height={32} />

        <form onSubmit={handleSave}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <NormalTextField
                name="startDate"
                label="Start Date"
                type="date"
                fullWidth
                required
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <NormalTextField
                name="startTime"
                label="Start Time"
                type="time"
                fullWidth
                defaultValue="00:00"
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isEndless}
                    onChange={(e) => setIsEndless(e.target.checked)}
                  />
                }
                label="Endless subscription"
              />
            </Grid>

            {!isEndless && (
              <>
                <Grid item xs={6}>
                  <NormalTextField
                    name="endDate"
                    label="End Date"
                    type="date"
                    fullWidth
                    required={!isEndless}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <NormalTextField
                    name="endTime"
                    label="End Time"
                    type="time"
                    fullWidth
                    defaultValue="23:59"
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <NormalTextField
                select
                name="status"
                label="Status"
                fullWidth
                defaultValue={SubscriptionStatus.ACTIVE}
                SelectProps={{
                  native: true
                }}
              >
                {statusOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </NormalTextField>
            </Grid>
          </Grid>

          <SumbitButtons 
            onClose={onClose} 
            isLoading={isLoading}
            onSaveLabel="Create"
          />
        </form>
      </RoundedPanel>
    </Modal>
  )
} 